import {
  LayoutGrid,
  BookOpen,
  MessageSquare,
  Users,
  Landmark,
  CreditCard,
  CalendarDays,
  FolderOpen,
  LifeBuoy,
  SquareCheck,
  Code2,
} from "lucide-react";
import {
  GoogleGMark,
  MicrosoftLogo,
  SlackLogo,
  TeamsLogo,
  SalesforceLogo,
  HubSpotLogo,
  QuickBooksLogo,
  XeroLogo,
  StripeLogo,
  SquareLogo,
  CalendarLogo,
  OutlookLogo,
  CalendlyLogo,
  GmailLogo,
  DriveLogo,
  OneDriveLogo,
  SharePointLogo,
  DropboxLogo,
  ZendeskLogo,
  IntercomLogo,
  AsanaLogo,
  TrelloLogo,
  NotionLogo,
  JiraLogo,
  GitHubLogo,
} from "@/components/landing/integration-logos";

export const CATEGORIES = [
  { id: "all", label: "All", icon: LayoutGrid },
  { id: "productivity", label: "Productivity", icon: BookOpen },
  { id: "communication", label: "Communication", icon: MessageSquare },
  { id: "crm", label: "CRM", icon: Users },
  { id: "finance", label: "Finance & Accounting", icon: Landmark },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "calendar", label: "Calendar & Scheduling", icon: CalendarDays },
  { id: "storage", label: "Storage & Documents", icon: FolderOpen },
  { id: "support", label: "Customer Support", icon: LifeBuoy },
  { id: "project", label: "Project Management", icon: SquareCheck },
  { id: "developer", label: "Developer Tools", icon: Code2 },
];

// apiSupported = the vendor publishes a legitimate programmatic interface.
// connectorStatus = whether Nova's connector is built ("available" | "coming-soon").
export const INTEGRATIONS = [
  { id: "google-workspace", name: "Google Workspace", vendor: "Google", category: "productivity",
    description: "Docs, Sheets, Drive, Gmail & more.",
    capabilities: ["Documents", "Spreadsheets", "Files", "Email", "Calendar"],
    authType: "OAuth 2.0 + REST API", apiSupported: true, connectorStatus: "available", statusType: "oauth", Logo: GoogleGMark },
  { id: "microsoft-365", name: "Microsoft 365", vendor: "Microsoft", category: "productivity",
    description: "Outlook, Teams, Office apps & more.",
    capabilities: ["Documents", "Spreadsheets", "Email", "Teams", "OneDrive"],
    authType: "OAuth 2.0 + Microsoft Graph", apiSupported: true, connectorStatus: "available", statusType: "oauth", Logo: MicrosoftLogo },
  { id: "slack", name: "Slack", vendor: "Salesforce", category: "communication",
    description: "Channels, DMs, notifications & more.",
    capabilities: ["Messages", "Channels", "Workflow alerts", "Notifications"],
    authType: "OAuth 2.0 + Web API", apiSupported: true, connectorStatus: "available", statusType: "api", Logo: SlackLogo },
  { id: "microsoft-teams", name: "Microsoft Teams", vendor: "Microsoft", category: "communication",
    description: "Chat, meetings & team alerts.",
    capabilities: ["Messages", "Channels", "Meetings", "Notifications"],
    authType: "OAuth 2.0 + Microsoft Graph", apiSupported: true, connectorStatus: "available", statusType: "oauth", Logo: TeamsLogo },
  { id: "gmail", name: "Gmail", vendor: "Google", category: "communication",
    description: "Send, receive and automate emails.",
    capabilities: ["Email", "Threads", "Labels", "Follow-ups"],
    authType: "OAuth 2.0 + Gmail API", apiSupported: true, connectorStatus: "available", statusType: "oauth", Logo: GmailLogo },
  { id: "salesforce", name: "Salesforce", vendor: "Salesforce", category: "crm",
    description: "Leads, contacts, opportunities & more.",
    capabilities: ["Contacts", "Leads", "Companies", "Opportunities", "Activities"],
    authType: "OAuth 2.0 + REST API", apiSupported: true, connectorStatus: "available", statusType: "oauth", Logo: SalesforceLogo },
  { id: "hubspot", name: "HubSpot", vendor: "HubSpot", category: "crm",
    description: "Contacts, companies, deals & more.",
    capabilities: ["Contacts", "Companies", "Deals", "Pipelines"],
    authType: "OAuth 2.0 + REST API", apiSupported: true, connectorStatus: "available", statusType: "custom", Logo: HubSpotLogo },
  { id: "quickbooks", name: "QuickBooks", vendor: "Intuit", category: "finance",
    description: "Customers, invoices, payments & more.",
    capabilities: ["Customers", "Vendors", "Invoices", "Payment status", "Accounting records"],
    authType: "OAuth 2.0 + REST API", apiSupported: true, connectorStatus: "available", statusType: "api", Logo: QuickBooksLogo },
  { id: "xero", name: "Xero", vendor: "Xero", category: "finance",
    description: "Invoices, bills & bank transactions.",
    capabilities: ["Invoices", "Bills", "Contacts", "Bank transactions"],
    authType: "OAuth 2.0 + REST API", apiSupported: true, connectorStatus: "available", statusType: "oauth", Logo: XeroLogo },
  { id: "stripe", name: "Stripe", vendor: "Stripe", category: "payments",
    description: "Accept payments and subscriptions.",
    capabilities: ["Transactions", "Payment status", "Subscriptions", "Customer payments"],
    authType: "REST API + Webhooks", apiSupported: true, connectorStatus: "available", statusType: "api", Logo: StripeLogo },
  { id: "square", name: "Square", vendor: "Block", category: "payments",
    description: "Payments, orders & invoices.",
    capabilities: ["Payments", "Orders", "Invoices", "Customers"],
    authType: "OAuth 2.0 + REST API", apiSupported: true, connectorStatus: "available", statusType: "oauth", Logo: SquareLogo },
  { id: "google-calendar", name: "Google Calendar", vendor: "Google", category: "calendar",
    description: "Create, manage and update events.",
    capabilities: ["Events", "Availability", "Scheduling", "Reminders"],
    authType: "OAuth 2.0 + REST API", apiSupported: true, connectorStatus: "available", statusType: "oauth", Logo: CalendarLogo },
  { id: "outlook-calendar", name: "Outlook Calendar", vendor: "Microsoft", category: "calendar",
    description: "Events, availability & reminders.",
    capabilities: ["Events", "Availability", "Reminders"],
    authType: "OAuth 2.0 + Microsoft Graph", apiSupported: true, connectorStatus: "available", statusType: "oauth", Logo: OutlookLogo },
  { id: "calendly", name: "Calendly", vendor: "Calendly", category: "calendar",
    description: "Bookings & scheduling links.",
    capabilities: ["Bookings", "Event types", "Invitees"],
    authType: "OAuth 2.0 + REST API", apiSupported: true, connectorStatus: "available", statusType: "custom", Logo: CalendlyLogo },
  { id: "google-drive", name: "Google Drive", vendor: "Google", category: "storage",
    description: "Files, folders & document retrieval.",
    capabilities: ["Files", "Folders", "Uploads", "Document retrieval"],
    authType: "OAuth 2.0 + REST API", apiSupported: true, connectorStatus: "available", statusType: "oauth", Logo: DriveLogo },
  { id: "onedrive", name: "OneDrive", vendor: "Microsoft", category: "storage",
    description: "Store, share and manage files.",
    capabilities: ["Files", "Folders", "Sharing", "Uploads"],
    authType: "OAuth 2.0 + Microsoft Graph", apiSupported: true, connectorStatus: "available", statusType: "api", Logo: OneDriveLogo },
  { id: "sharepoint", name: "SharePoint", vendor: "Microsoft", category: "storage",
    description: "Sites, libraries & documents.",
    capabilities: ["Sites", "Libraries", "Documents", "Lists"],
    authType: "OAuth 2.0 + Microsoft Graph", apiSupported: true, connectorStatus: "available", statusType: "oauth", Logo: SharePointLogo },
  { id: "dropbox", name: "Dropbox", vendor: "Dropbox", category: "storage",
    description: "Sync and share files securely.",
    capabilities: ["Files", "Folders", "Sharing", "Sync"],
    authType: "OAuth 2.0 + REST API", apiSupported: true, connectorStatus: "available", statusType: "custom", Logo: DropboxLogo },
  { id: "zendesk", name: "Zendesk", vendor: "Zendesk", category: "support",
    description: "Tickets, conversations & SLAs.",
    capabilities: ["Tickets", "Conversations", "Customer requests", "Status updates"],
    authType: "OAuth 2.0 + REST API", apiSupported: true, connectorStatus: "available", statusType: "oauth", Logo: ZendeskLogo },
  { id: "intercom", name: "Intercom", vendor: "Intercom", category: "support",
    description: "Customer chats & conversations.",
    capabilities: ["Conversations", "Messages", "Contacts"],
    authType: "OAuth 2.0 + REST API", apiSupported: true, connectorStatus: "coming-soon", statusType: "soon", Logo: IntercomLogo },
  { id: "asana", name: "Asana", vendor: "Asana", category: "project",
    description: "Tasks, projects & due dates.",
    capabilities: ["Tasks", "Assignments", "Due dates", "Status", "Comments"],
    authType: "OAuth 2.0 + REST API", apiSupported: true, connectorStatus: "available", statusType: "oauth", Logo: AsanaLogo },
  { id: "trello", name: "Trello", vendor: "Atlassian", category: "project",
    description: "Boards, cards & checklists.",
    capabilities: ["Boards", "Cards", "Checklists", "Due dates"],
    authType: "OAuth 1.0 + REST API", apiSupported: true, connectorStatus: "available", statusType: "api", Logo: TrelloLogo },
  { id: "notion", name: "Notion", vendor: "Notion", category: "project",
    description: "Docs, wikis, tasks & collaboration.",
    capabilities: ["Pages", "Databases", "Tasks", "Wikis"],
    authType: "OAuth 2.0 + REST API", apiSupported: true, connectorStatus: "coming-soon", statusType: "soon", Logo: NotionLogo },
  { id: "jira", name: "Jira", vendor: "Atlassian", category: "developer",
    description: "Issues, sprints & releases.",
    capabilities: ["Issues", "Sprints", "Releases", "Notifications"],
    authType: "OAuth 2.0 + REST API", apiSupported: true, connectorStatus: "available", statusType: "oauth", Logo: JiraLogo },
  { id: "github", name: "GitHub", vendor: "Microsoft", category: "developer",
    description: "Repos, issues & deployments.",
    capabilities: ["Repositories", "Issues", "Pull requests", "Deployments", "Webhooks"],
    authType: "OAuth 2.0 + REST/GraphQL API", apiSupported: true, connectorStatus: "available", statusType: "api", Logo: GitHubLogo },
];

export const categoryLabel = (id) => CATEGORIES.find((c) => c.id === id)?.label || id;

export const searchIntegrations = (list, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return list;
  return list.filter((i) =>
    [i.name, i.vendor, categoryLabel(i.category), i.description, ...i.capabilities]
      .join(" ")
      .toLowerCase()
      .includes(q)
  );
};
